export function clampToBounds(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function moveWithinBounds(position, direction, speed, deltaSeconds, bounds) {
  const normalizedDirection = normalizeVector(direction);

  return {
    x: clampToBounds(
      position.x + normalizedDirection.x * speed * deltaSeconds,
      bounds.minX,
      bounds.maxX
    ),
    y: clampToBounds(
      position.y + normalizedDirection.y * speed * deltaSeconds,
      bounds.minY,
      bounds.maxY
    ),
  };
}

export function normalizeVector(vector = { x: 0, y: 0 }) {
  const length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);

  if (length === 0) {
    return { x: 0, y: 0 };
  }

  return {
    x: vector.x / length,
    y: vector.y / length,
  };
}

export function playerReachedFlag(player, flag) {
  const dx = Math.abs(player.x - flag.x);
  const dy = Math.abs(player.y - flag.y);

  return dx <= flag.width / 2 && dy <= flag.height / 2;
}

export function getFacingDirection(direction, fallbackDirection = 'down') {
  if (direction.x === 0 && direction.y === 0) {
    return fallbackDirection;
  }

  if (Math.abs(direction.x) > Math.abs(direction.y)) {
    return direction.x > 0 ? 'right' : 'left';
  }

  return direction.y > 0 ? 'down' : 'up';
}

export function getVectorFacingDirection(direction, fallbackDirection = 'down') {
  return getFacingDirection(normalizeVector(direction), fallbackDirection);
}

export function getMovementFacingDirection({
  movementVector,
  aimVector,
  fallbackDirection = 'down',
}) {
  if (movementVector?.x !== 0 || movementVector?.y !== 0) {
    return getVectorFacingDirection(movementVector, fallbackDirection);
  }

  return getVectorFacingDirection(aimVector ?? { x: 0, y: 0 }, fallbackDirection);
}

export function getMovementAimVector({
  movementVector,
  fallbackVector = { x: 0, y: 1 },
}) {
  const normalizedMovement = normalizeVector(movementVector);

  if (normalizedMovement.x !== 0 || normalizedMovement.y !== 0) {
    return normalizedMovement;
  }

  const normalizedFallback = normalizeVector(fallbackVector);

  if (normalizedFallback.x !== 0 || normalizedFallback.y !== 0) {
    return normalizedFallback;
  }

  return { x: 0, y: 1 };
}

export function pointIsInsideZone(point, zone) {
  const minX = zone.x - zone.width / 2;
  const maxX = zone.x + zone.width / 2;
  const minY = zone.y - zone.height / 2;
  const maxY = zone.y + zone.height / 2;

  return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
}

export function getTerrainSpeedMultiplier(position, terrains = []) {
  return terrains.reduce((currentMultiplier, terrain) => {
    if (!pointIsInsideZone(position, terrain)) {
      return currentMultiplier;
    }

    return Math.min(currentMultiplier, terrain.speedMultiplier ?? 1);
  }, 1);
}

export function calculateMissionScore({
  elapsedMs,
  alertCount = 0,
  enemyDefeatCount = 0,
  baseScore = 1200,
  timeDivisorMs = 7,
  alertPenalty = 75,
  enemyDefeatBonus = 90,
}) {
  const timePenalty = elapsedMs / timeDivisorMs;
  const score = baseScore - timePenalty - alertCount * alertPenalty + enemyDefeatCount * enemyDefeatBonus;

  return Math.max(0, Math.round(score));
}

export function calculateMissionScoreBreakdown({
  elapsedMs,
  alertCount = 0,
  enemyDefeatCount = 0,
  remainingLives = 0,
  baseScore = 1200,
  timeDivisorMs = 7,
  alertPenalty = 75,
  enemyDefeatBonus = 90,
}) {
  const timePenalty = Math.round(elapsedMs / timeDivisorMs);
  const alertPenaltyPoints = alertCount * alertPenalty;
  const enemyDefeatBonusPoints = enemyDefeatCount * enemyDefeatBonus;
  const score = Math.max(
    0,
    Math.round(baseScore - timePenalty - alertPenaltyPoints + enemyDefeatBonusPoints)
  );

  return {
    baseScore,
    timePenalty,
    alertPenaltyPoints,
    enemyDefeatBonusPoints,
    remainingLives,
    score,
  };
}

export function getMissionRank({
  score = 0,
  elapsedMs = 0,
  alertCount = 0,
  remainingLives = 0,
}) {
  if (alertCount === 0 && remainingLives > 0 && elapsedMs <= 45000 && score >= 1000) {
    return 'ghost';
  }

  if (score >= 900 && alertCount <= 1) {
    return 'veteran';
  }

  if (score >= 650) {
    return 'clear';
  }

  return 'survivor';
}

export function getCooldownState({
  currentTimeMs,
  lastFireTimeMs = 0,
  cooldownMs,
}) {
  const elapsedMs = Math.max(0, currentTimeMs - lastFireTimeMs);
  const remainingMs = Math.max(0, cooldownMs - elapsedMs);

  return {
    isReady: remainingMs === 0,
    progress: cooldownMs > 0 ? Math.min(1, elapsedMs / cooldownMs) : 1,
    remainingMs,
  };
}

export function getFeedbackIntensity(eventType, { reducedMotion = false } = {}) {
  const intensities = {
    rifle: { shakeMs: 55, shakeAmount: 0.0018, hitPauseMs: 0 },
    grenade: { shakeMs: 120, shakeAmount: 0.004, hitPauseMs: 20 },
    melee: { shakeMs: 65, shakeAmount: 0.0022, hitPauseMs: 12 },
    enemyHit: { shakeMs: 45, shakeAmount: 0.0015, hitPauseMs: 10 },
    playerDamage: { shakeMs: 140, shakeAmount: 0.0045, hitPauseMs: 28 },
    missionComplete: { shakeMs: 160, shakeAmount: 0.003, hitPauseMs: 0 },
  };
  const fallback = { shakeMs: 0, shakeAmount: 0, hitPauseMs: 0 };
  const intensity = intensities[eventType] ?? fallback;

  if (!reducedMotion) {
    return intensity;
  }

  return {
    shakeMs: 0,
    shakeAmount: 0,
    hitPauseMs: Math.min(10, intensity.hitPauseMs),
  };
}

export const RANGER_WEAPONS = {
  rifle: {
    type: 'rifle',
    cooldownMs: 260,
    speed: 340,
    lifetimeMs: 560,
    damage: 1,
    width: 15,
    height: 4,
  },
  grenade: {
    type: 'grenade',
    cooldownMs: 1450,
    speed: 205,
    lifetimeMs: 620,
    damage: 2,
    radius: 7,
    blastRadius: 48,
  },
  melee: {
    type: 'melee',
    cooldownMs: 520,
    range: 54,
    angleDegrees: 86,
    damage: 1,
  },
};

export function calculateActionPosition(playerPosition, facingDirection, range = 32) {
  const direction = getDirectionVector(facingDirection);

  return {
    x: playerPosition.x + direction.x * range,
    y: playerPosition.y + direction.y * range,
  };
}

export function calculateVectorActionPosition(playerPosition, aimVector, range = 32) {
  const direction = normalizeVector(aimVector);
  const fallback = direction.x === 0 && direction.y === 0 ? { x: 0, y: 1 } : direction;

  return {
    x: playerPosition.x + fallback.x * range,
    y: playerPosition.y + fallback.y * range,
  };
}

export function getDirectionVector(facingDirection) {
  const directionMap = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
  };

  return directionMap[facingDirection] ?? directionMap.down;
}

export function getDistanceBetweenPoints(pointA, pointB) {
  const dx = pointA.x - pointB.x;
  const dy = pointA.y - pointB.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function shouldFireWeapon({
  actionPressed,
  currentTimeMs,
  lastFireTimeMs,
  cooldownMs,
}) {
  return Boolean(actionPressed) && currentTimeMs - lastFireTimeMs >= cooldownMs;
}

export function createWeaponProjectiles({
  weaponType = 'rifle',
  origin,
  facingDirection = 'down',
  aimVector,
  projectileIdStart = 0,
}) {
  const weapon = RANGER_WEAPONS[weaponType] ?? RANGER_WEAPONS.rifle;
  const direction = normalizeVector(aimVector ?? getDirectionVector(facingDirection));
  const spawn = calculateVectorActionPosition(origin, direction, weaponType === 'grenade' ? 23 : 24);
  const resolvedFacingDirection = getVectorFacingDirection(direction, facingDirection);

  return [{
    id: projectileIdStart,
    weaponType: weapon.type,
    x: spawn.x,
    y: spawn.y,
    vx: direction.x * weapon.speed,
    vy: direction.y * weapon.speed,
    facingDirection: resolvedFacingDirection,
    angle: Math.atan2(direction.y, direction.x),
    damage: weapon.damage,
    lifetimeMs: weapon.lifetimeMs,
    blastRadius: weapon.blastRadius ?? 0,
    width: weapon.width,
    height: weapon.height,
    radius: weapon.radius,
  }];
}

export function getPatrolFacingDirection(enemyData) {
  const direction = enemyData.direction ?? 1;

  if (enemyData.axis === 'vertical') {
    return direction > 0 ? 'down' : 'up';
  }

  return direction > 0 ? 'right' : 'left';
}

export function getDirectionAngle(facingDirection) {
  const angleMap = {
    right: 0,
    down: Math.PI / 2,
    left: Math.PI,
    up: -Math.PI / 2,
  };

  return angleMap[facingDirection] ?? angleMap.down;
}

export function normalizeAngleDelta(angle) {
  let nextAngle = angle;

  while (nextAngle > Math.PI) {
    nextAngle -= Math.PI * 2;
  }

  while (nextAngle < -Math.PI) {
    nextAngle += Math.PI * 2;
  }

  return nextAngle;
}

export function isPointInSightCone({
  viewer,
  target,
  facingDirection = 'down',
  range = 150,
  angleDegrees = 58,
  rangeMultiplier = 1,
}) {
  const distance = getDistanceBetweenPoints(viewer, target);

  if (distance > range * rangeMultiplier) {
    return false;
  }

  const targetAngle = Math.atan2(target.y - viewer.y, target.x - viewer.x);
  const facingAngle = getDirectionAngle(facingDirection);
  const delta = Math.abs(normalizeAngleDelta(targetAngle - facingAngle));
  const halfAngle = (angleDegrees * Math.PI / 180) / 2;

  return delta <= halfAngle;
}

export function canDetectTarget({
  viewer,
  target,
  facingDirection = 'down',
  range = 150,
  angleDegrees = 58,
  rangeMultiplier = 1,
  blockers = [],
}) {
  return isPointInSightCone({
    viewer,
    target,
    facingDirection,
    range,
    angleDegrees,
    rangeMultiplier,
  }) && hasLineOfSight(viewer, target, blockers);
}

export function createAimedProjectile({
  origin,
  target,
  speed = 185,
}) {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance === 0) {
    return { vx: 0, vy: speed };
  }

  return {
    vx: (dx / distance) * speed,
    vy: (dy / distance) * speed,
  };
}

function lineSegmentsIntersect(a, b, c, d) {
  const ccw = (pointA, pointB, pointC) =>
    (pointC.y - pointA.y) * (pointB.x - pointA.x) >
    (pointB.y - pointA.y) * (pointC.x - pointA.x);

  return ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d);
}

export function lineIntersectsRect(start, end, rect) {
  if (pointIsInsideZone(start, rect) || pointIsInsideZone(end, rect)) {
    return true;
  }

  const minX = rect.x - rect.width / 2;
  const maxX = rect.x + rect.width / 2;
  const minY = rect.y - rect.height / 2;
  const maxY = rect.y + rect.height / 2;
  const corners = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ];

  return corners.some((corner, index) => lineSegmentsIntersect(
    start,
    end,
    corner,
    corners[(index + 1) % corners.length]
  ));
}

export function hasLineOfSight(start, end, blockers = []) {
  return !blockers.some((blocker) => (
    !pointIsInsideZone(start, blocker) && lineIntersectsRect(start, end, blocker)
  ));
}

export function advanceProjectilePosition(projectile, deltaMs) {
  const deltaSeconds = deltaMs / 1000;

  return {
    x: projectile.x + projectile.vx * deltaSeconds,
    y: projectile.y + projectile.vy * deltaSeconds,
  };
}

export function projectileIsOutsideBounds(projectile, bounds, padding = 24) {
  return (
    projectile.x < -padding ||
    projectile.x > bounds.width + padding ||
    projectile.y < -padding ||
    projectile.y > bounds.height + padding
  );
}

export function getPatrolMotion(enemyData, position) {
  const direction = enemyData.direction ?? 1;
  const offset = enemyData.axis === 'vertical'
    ? position.y - enemyData.originY
    : position.x - enemyData.originX;
  const isWaiting = (enemyData.waitUntilMs ?? 0) > (enemyData.currentTimeMs ?? 0);

  if (isWaiting) {
    return {
      direction,
      facingDirection: enemyData.facingDirection ?? getPatrolFacingDirection(enemyData),
      shouldStartTurnWait: false,
      velocityX: 0,
      velocityY: 0,
    };
  }

  const isAtPatrolBound = Math.abs(offset) >= enemyData.range;
  const nextDirection = isAtPatrolBound ? direction * -1 : direction;
  const velocity = enemyData.speed * nextDirection;

  return {
    direction: nextDirection,
    facingDirection: isAtPatrolBound
      ? (enemyData.facingDirection ?? getPatrolFacingDirection(enemyData))
      : getPatrolFacingDirection({
        ...enemyData,
        direction: nextDirection,
      }),
    shouldStartTurnWait: isAtPatrolBound,
    velocityX: enemyData.axis === 'horizontal' ? velocity : 0,
    velocityY: enemyData.axis === 'vertical' ? velocity : 0,
  };
}
